import { useEnterpriseContactForm } from "@/hooks/useEnterpriseContactForm";
import routes from "@/router/routes";
import { ArrowLeftIcon, Loader2, SendIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import ContactField from "./ContactField";

const EnterpriseContactForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        formData,
        errors,
        handleInputChange,
        handleBlur,
        handleSubmit,
        isSuccess,
    } = useEnterpriseContactForm();

    const isFormValid = useMemo(
        () =>
            Object.values(errors).every((error) => !error) &&
            formData.firstName.trim() !== "" &&
            formData.lastName.trim() !== "" &&
            formData.phone.trim() !== "" &&
            formData.email.trim() !== "" &&
            formData.companyName.trim() !== "" &&
            formData.siret.trim() !== "",
        [errors, formData],
    );

    return (
        <div className="rounded-lg border border-primary-dark/50 bg-white p-8">
            {isSuccess ? (
                <div className="flex flex-col items-center gap-6">
                    <p className="text-center text-text-body">
                        Votre mail a bien été envoyé, notre équipe vous
                        recontactera dans les plus brefs délais.
                    </p>
                    <Button
                        onClick={() => navigate(routes.selectLicense)}
                        type="button"
                        variant="outline"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        {t("enterpriseForm.back")}
                    </Button>
                </div>
            ) : (
                <form
                    className="flex flex-col gap-6"
                    onSubmit={handleSubmit.mutate}
                >
                    <div className="flex gap-6">
                        <ContactField
                            error={errors.firstName}
                            id="firstName"
                            label={`${t("enterpriseForm.firstName")} *`}
                            onBlur={() => handleBlur("firstName")}
                            onChange={(value: string) =>
                                handleInputChange("firstName", value)
                            }
                            type="text"
                            value={formData.firstName}
                        />
                        <ContactField
                            error={errors.lastName}
                            id="lastName"
                            label={`${t("enterpriseForm.lastName")} *`}
                            onBlur={() => handleBlur("lastName")}
                            onChange={(value: string) =>
                                handleInputChange("lastName", value)
                            }
                            type="text"
                            value={formData.lastName}
                        />
                    </div>
                    <div className="flex gap-6">
                        <ContactField
                            error={errors.phone}
                            id="phone"
                            label={`${t("enterpriseForm.phone")} *`}
                            onBlur={() => handleBlur("phone")}
                            onChange={(value: string) =>
                                handleInputChange("phone", value)
                            }
                            type="tel"
                            value={formData.phone}
                        />
                        <ContactField
                            error={errors.email}
                            id="email"
                            label={`${t("enterpriseForm.email")} *`}
                            onBlur={() => handleBlur("email")}
                            onChange={(value: string) =>
                                handleInputChange("email", value)
                            }
                            type="email"
                            value={formData.email}
                        />
                    </div>
                    <div className="flex gap-6">
                        <ContactField
                            error={errors.companyName}
                            id="companyName"
                            label={`${t("enterpriseForm.companyName")} *`}
                            onBlur={() => handleBlur("companyName")}
                            onChange={(value: string) =>
                                handleInputChange("companyName", value)
                            }
                            type="text"
                            value={formData.companyName}
                        />
                        <ContactField
                            error={errors.siret}
                            id="siret"
                            label={`${t("enterpriseForm.siret")} *`}
                            onBlur={() => handleBlur("siret")}
                            onChange={(value: string) =>
                                handleInputChange("siret", value)
                            }
                            type="text"
                            value={formData.siret}
                        />
                    </div>
                    <div className="flex justify-between gap-4 pt-4">
                        <Button
                            onClick={() => navigate(routes.selectLicense)}
                            type="button"
                            variant="outline"
                        >
                            <ArrowLeftIcon className="h-4 w-4" />
                            {t("enterpriseForm.back")}
                        </Button>
                        <Button
                            disabled={!isFormValid || handleSubmit.isPending}
                            type="submit"
                        >
                            {handleSubmit.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <SendIcon className="h-4 w-4" />
                                    {t("enterpriseForm.submit")}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default EnterpriseContactForm;
